import constants from 'util/data/constants';
import router from 'uav-router';
import deepCloneObject from 'util/data/deep-clone-object';
import modal from 'views/modal';
import ToolboxPicker from 'views/toolbox-picker';
import toolboxPreview from 'views/toolbox-preview';
import ErrorLog from 'views/error-log';
import uploadModel from 'models/upload-model';
import closestNumber from 'util/numbers/closest-number';
import {styleConfig} from 'models/style-model';
import permissionsModel from 'models/permissions-model';
import {ToolboxId} from 'views/toolbox/tool-representation';
import draftsModel from 'models/drafts-model';
import MreactComponent from 'util/dom/mreact-component';
import ButtonElement from 'views/react/Button';

const PROJECT_CONTROL_LABEL = '_projectId';
const PROGRESS_DASHBOARD_FLAG = 'Progress Dashboard';
const BATCH_MODIFY_FLAG = 'Batch modify';
const TOOLKIT_MODIFY_FLAG = 'Disable Toolkit Modifications';
const EXPORTS_FLAG = 'Disable Data Exports';
const TOOL_STYLES = 'toolStyles';

const controlTypeNameToId = constants.controlTypeNameToId;

class ToolboxModel {

    constructor() {
        window.model = this;

        this.reset();
        const defaultPointStyle = {
            type: 'symbol',
            layout: {
                // icon-size units are specified as a fraction of the original icon size.
                // To make an icon render at 32px wide regardless of its original width,
                // We set icon-size to 32 / _sourceWidthPx, where _sourceWidthPx is the
                // natural width of the source image.
                'icon-size': ['/', 32, ['get', '_sourceWidthPx']],
                'icon-anchor': 'bottom',
                'icon-allow-overlap': true,
                'icon-pitch-alignment': 'viewport'
            }
        };

        this.defaultStyles = {
            symbol: defaultPointStyle,
            filepicker: defaultPointStyle,
            imodel: defaultPointStyle,
            fill: {
                type: 'fill',
                paint: {
                    'fill-color': '#70bfbf',
                    'fill-outline-color': '#303840',
                    'fill-opacity': 1
                }
            },
            polyline: {
                type: 'line',
                paint: {
                    'line-color': '#f2bf66',
                    'line-width': 3,
                    'line-opacity': 1
                }
            },
            boundary: {
                type: 'line',
                paint: {
                    'line-color': '#f2bf66',
                    'line-width': 3,
                    'line-opacity': 1
                }
            },
            text: {
                type: 'symbol',
                layout: {
                    'text-field': 'TEXT',
                    'text-font': ['museo'],
                    'text-anchor': 'center',
                    'text-max-width': 10000,
                    'text-allow-overlap': true,
                    'text-size': 20,
                    'text-rotate': 0,
                    'text-pitch-alignment': 'map',
                    'text-rotation-alignment': 'map'
                },
                'paint': {
                    'text-color': '#ffffff',
                    'text-halo-color': '#000000',
                    'text-halo-width': 1
                }
            },
            project: {
                type: 'fill',
                paint: {
                    'fill-color': '#70bfbf',
                    'fill-outline-color': '#303840',
                    'fill-opacity': 1
                }
            }
        };

        // Controls that can only have 1 instance in an assetFrom.
        this.controlOneAllowed = {
            [controlTypeNameToId.name]: true,
            [controlTypeNameToId.project]: true,
            [controlTypeNameToId.imodel]: true
        };

        this.linkDescriptions = {
            filepicker: {
                [controlTypeNameToId.coordinates]: 'file coordinates',
                [controlTypeNameToId.dropdown]: 'media ID',
                [controlTypeNameToId.file]: 'file asset',
                [controlTypeNameToId.name]: 'filename',
                [controlTypeNameToId.number]: 'file count',
                [controlTypeNameToId.paragraph]: 'filename',
                [controlTypeNameToId.text]: 'filename',
                [controlTypeNameToId.toggle]: 'presence of the file',
                [controlTypeNameToId.URL]: 'url',
                [controlTypeNameToId.date]: 'capture date'
            },
            plan: {
                [controlTypeNameToId.file]: 'plan asset',
                [controlTypeNameToId.name]: 'plan title',
                [controlTypeNameToId.paragraph]: 'plan title',
                [controlTypeNameToId.plan]: 'plan asset',
                [controlTypeNameToId.text]: 'plan title',
                [controlTypeNameToId.URL]: 'plan tileset url',
                [controlTypeNameToId.date]: 'created date'
            },
            survey: {
                [controlTypeNameToId.name]: 'survey title',
                [controlTypeNameToId.paragraph]: 'survey title',
                [controlTypeNameToId.survey]: 'survey asset',
                [controlTypeNameToId.text]: 'survey title',
                [controlTypeNameToId.URL]: 'survey tileset URL',
                [controlTypeNameToId.date]: 'survey date'
            },
            symbol: {
                [controlTypeNameToId.coordinates]: 'point coordinates',
                [controlTypeNameToId.name]: 'point coordinates',
                [controlTypeNameToId.paragraph]: 'point coordinates',
                [controlTypeNameToId.text]: 'point coordinates',
                [controlTypeNameToId.toggle]: 'presence of the point'
            },
            polyline: {
                [controlTypeNameToId.length]: 'line length',
                [controlTypeNameToId.name]: 'line length',
                [controlTypeNameToId.number]: 'line length',
                [controlTypeNameToId.paragraph]: 'line length',
                [controlTypeNameToId.text]: 'line length'
            },
            polygon: {
                [controlTypeNameToId.area]: 'polygon area',
                [controlTypeNameToId.length]: 'polygon area',
                [controlTypeNameToId.name]: 'polygon area',
                [controlTypeNameToId.number]: 'polygon area',
                [controlTypeNameToId.paragraph]: 'polygon area',
                [controlTypeNameToId.text]: 'polygon area',
                [controlTypeNameToId.volume]: 'polygon volume'
            },
            text: {
                [controlTypeNameToId.name]: 'text value',
                [controlTypeNameToId.paragraph]: 'text value',
                [controlTypeNameToId.text]: 'text value'
            },
            project: {
                [controlTypeNameToId.name]: 'project name',
                [controlTypeNameToId.paragraph]: 'project name',
                [controlTypeNameToId.text]: 'project name',
                [controlTypeNameToId.project]: 'project ID'
            },
            action: {
                [controlTypeNameToId.name]: 'work item name',
                [controlTypeNameToId.paragraph]: 'work item name',
                [controlTypeNameToId.text]: 'work item name'
            },
            imodel: {
                [controlTypeNameToId.coordinates]: 'iModel coordinates',
                [controlTypeNameToId.name]: 'iModel name',
                [controlTypeNameToId.paragraph]: 'iModel description',
                [controlTypeNameToId.text]: 'iModel description',
                [controlTypeNameToId.imodel]: 'iModel asset',
                [controlTypeNameToId.date]: 'iModel created date'
            }
        };

        this.isCommentsOrLinks = {
            [controlTypeNameToId.links]: true,
            [controlTypeNameToId.comments]: true
        };

        this.hasExpressionOption = {
            [controlTypeNameToId.name]: true,
            [controlTypeNameToId.text]: true,
            [controlTypeNameToId.paragraph]: true,
            [controlTypeNameToId.dropdown]: true,
            [controlTypeNameToId.radio]: true,
            [controlTypeNameToId.length]: true,
            [controlTypeNameToId.area]: true,
            [controlTypeNameToId.volume]: true,
            [controlTypeNameToId.number]: true,
            [controlTypeNameToId.date]: true,
            [controlTypeNameToId.multiselect]: true,
            [controlTypeNameToId.multitext]: true,
            [controlTypeNameToId.request]: true
        };

        this.hasPDFOption = {
            [controlTypeNameToId.name]: true,
            [controlTypeNameToId.text]: true,
            [controlTypeNameToId.paragraph]: true,
            [controlTypeNameToId.dropdown]: true,
            [controlTypeNameToId.radio]: true,
            [controlTypeNameToId.length]: true,
            [controlTypeNameToId.area]: true,
            [controlTypeNameToId.volume]: true,
            [controlTypeNameToId.number]: true,
            [controlTypeNameToId.date]: true
        };

        // "protected tools" are tools that are auto-generated by
        // the toolbox editor and included in every toolbox.
        // They can't be edited in the UI.
        this.isProtectedTool = {
            Comment: true,
            Image: true,
            Audio: true,
            Video: true,
            File: true
        };

        this.isProtectedToolgroup = {
            Files: true
        };

        // These are asset types that were used multiple times in
        // various hand-written toolboxes back before the toolbox
        // editor existed. Because they were used in multiple
        // toolboxes, editing one of these asset types and then
        // publishing the toolbox would result in changes to
        // other toolboxes. Therefore editing them is disabled.
        this.sharedAssetTypes = {
            'uBOVzgW0ZTGZCB3Rzpt0': 1,
            'srNG1vbiRjqIsqwZ3TiG': 1,
            'h5Nlry0aI0smuRabNGor': 1,
            'WMdHjR3ZWV0IOKS2qdFm': 1,
            'g8EEwvX5yvneK450WeYJ': 1,
            'JrzOJ8g98jNhL4UUpxhx': 1,
            'f0gOas9SEcq3faMe1Z5n': 1,
            'Y07e2k8EI9tJ_frnSXgm': 1,
            'NotmRnHIyaaza-LB-sT6': 1,
            'Mo_LPbuE0MJz1AMHuOjz': 1,
            'cnONPjQrSaR6ewB8Rzok': 1,
            'shnHZh1wcuKmMaavfjTH': 1,
            'pBaXCaKoddCRwPF7p1oB': 1,
            '2yFT6LtKWU417AL5mhsP': 1,
            'PM8Ckf-h5oTtmir-AKQI': 1,
            'gSNuEa3X9X7DtSAfna5-': 1,
            'hiN7c3QYtYQQbFS1MpU9': 1,
            'SKje-bOgFl7SEfwrdKGE': 1,
            'O_lyPH53BoJ2jK9y-W82': 1,
            'pMiTcACNVsu3-1HYcK77': 1,
            'Ivt_L4D2AtkERyL0pbRk': 1,
            'i1UluUM-v0byjdNL_nwH': 1,
            'A54Bxer81kCau6Ipmq6V': 1,
            '_YJskMLDeviKX5CIXVKc': 1,
            'Rb0vYjspAFhsiJS9r-ei': 1,
            'FMkuc3NeHGZhrb0roWTI': 1,
            'eOa3CxHUkZSK6Owet43K': 1,
            'QXjJZRAxxVQ_RZ3oRpnJ': 1,
            'uY7RbD9c6QF1SGlF6-fp': 1,
            'zPMU6mUQY9Y4ZMmUvRz4': 1,
            'FJr7iSTjPQXLel4fmmBm': 1,
            '0hqDkae7wHqh4J6_O_FL': 1,
            'O_kbvT2j6pQm-KK5C6Rf': 1,
            '5ldXODAbyF-Qgg1MYxX0': 1,
            'xrN5lh_CHWuiCU_VnE7A': 1,
            'H7iEIvB5M03WM1WTH73-': 1,
            '7SndpeNjvqbFJF-Ir-he': 1,
            'IiEkZD4oGOPNJhnXUfaI': 1,
            'WtRYWf3J4R4MfRE4EugY': 1,
            'F0WE9BJUXd834ES6cRpX': 1,
            'waJQncpr1pXpKYjx-5qP': 1,
            'goPYISMQHtHQYZD_camB': 1,
            'LBjsmg3QZnNXQCGgYtQG': 1,
            'ZhUVoevmI_GcFOzMzlGA': 1,
            'LjLgtBBjUKALMuNaOkJu': 1,
            '-zFt0x3Sd0R7Zd9-oOva': 1,
            'Ed2lWWvqtOVE2pcwbboo': 1,
            '-G3JQ9Vl1qEhKT0sKP_i': 1,
            'a_qAqGcizjVZDdt6IRuB': 1,
            'gAdwpzqM89MQqIeFXtpe': 1,
            'l8ey7VqEUUSJRiWGpxCG': 1,
            '8hJXuN9gJN0cjeGsuU6g': 1
        };

        // All available toolbox feature flags
        this.toolboxFeatures = [
            BATCH_MODIFY_FLAG,
            TOOLKIT_MODIFY_FLAG,
            EXPORTS_FLAG
        ];

        this.toolGroupFeatures = [
            {
                key: TOOL_STYLES,
                label: 'Enable custom tool syles'
            }
        ];
        // The values of these control types
        // will be indexed by the back end for text search.
        this.doIndex = {
            dropdown: true,
            multiselect: true,
            multitext: true,
            name: true,
            paragraph: true,
            radio: true,
            text: true,
            URL: true
        };
    }

    // Normally, to find the field for a control, you use control.fieldName
    // and grab the field with that name. But during editing, multiple controls
    // could temporarily have the same fieldName, in which case it's ambiguous
    // which field is the right one for the control.
    //
    // To handle this, we find all fields that could be a valid field for the control.
    // If there are multiple, choose the one whose position in the fields
    // list is closer to the position of the control in the controls list.
    findFieldIndex(control, controlIndex, fields) {
        const controlTypeId = control.controlTypeId,
            candidateFieldIndices = [];

        fields.forEach((field, fieldIndex) => {

            // If the field name doesn't match the control label,
            // then we know this field is for a different control
            if (control.fieldName !== field.name) {
                return;
            }

            // But if the field name matches and the field
            // type matches the control type, then we might
            // have the right field for the control.
            if (typeof field.type === 'string') {

                if ({
                    [controlTypeNameToId.area]: 'area',
                    [controlTypeNameToId.color]: 'color',
                    [controlTypeNameToId.comments]: 'comments',
                    [controlTypeNameToId.date]: 'datetime',
                    [controlTypeNameToId.dateTime]: 'datetime',
                    [controlTypeNameToId.length]: 'length',
                    [controlTypeNameToId.links]: 'attachments',
                    [controlTypeNameToId.name]: 'name',
                    [controlTypeNameToId.number]: 'number',
                    [controlTypeNameToId.paragraph]: 'paragraph',
                    [controlTypeNameToId.request]: 'text',
                    [controlTypeNameToId.text]: 'text',
                    [controlTypeNameToId.toggle]: 'boolean',
                    [controlTypeNameToId.URL]: 'url',
                    [controlTypeNameToId.volume]: 'volume',
                    [controlTypeNameToId.asset]: 'asset'
                }[controlTypeId] === field.type) {

                    candidateFieldIndices.push(fieldIndex);

                }

            } else {

                // Some field types are more complicated, so we need to parse them to know what they represent.

                const verify = trueOrFalse => trueOrFalse && candidateFieldIndices.push(fieldIndex);
                switch (controlTypeId) {
                case controlTypeNameToId.asset:
                    verify(field.type.type === 'array' && field.type.items && field.type.items.type === 'objRef');
                    break;
                case controlTypeNameToId.coordinates:
                    verify(field.type.type === 'geometry' && field.type.geomType === 'Point');
                    break;
                case controlTypeNameToId.dropdown:
                case controlTypeNameToId.radio:
                    verify(field.type.type === 'enum');
                    break;
                case controlTypeNameToId.multiselect:
                    verify(field.type.type === 'array' && field.type.items && field.type.items.type === 'enum');
                    break;
                case controlTypeNameToId.multitext:
                    verify(field.type.type === 'array' && field.type.items && field.type.items.type === 'text');
                    break;
                case controlTypeNameToId.multiuser:
                    verify(field.type.type === 'array' && field.type.items && field.type.items.type === 'objRef' && field.type.items.objType === 'user');
                    break;
                case controlTypeNameToId.file:
                    verify(field.type.type === 'array' && field.type.items && field.type.items.type === 'objRef' && field.type.items.objType === 'media');
                    break;
                case controlTypeNameToId.plan:
                    verify(field.type.type === 'objRef' && field.type.objType === 'plan');
                    break;
                case controlTypeNameToId.project:
                    verify(field.type.type === 'objRef' && field.type.objType === 'project');
                    break;
                case controlTypeNameToId.survey:
                    verify(field.type.type === 'objRef' && field.type.objType === 'survey');
                    break;
                case controlTypeNameToId.user:
                    verify(field.type.type === 'objRef' && field.type.objType === 'user');
                    break;
                case controlTypeNameToId.place:
                    verify(field.type.type === 'object' && field.type.properties.placeIds);
                    break;
                case controlTypeNameToId.imodel:
                    verify(field.type.type === 'object' && field.type.properties.iModelId);
                    break;
                case controlTypeNameToId.URL:
                    verify(field.type.type === 'object');
                    break;
                }
            }
        });

        // If we found multiple candidate fields, use the position
        // of the field in the list to match it to the control.
        if (candidateFieldIndices.length > 1) {
            return closestNumber(controlIndex, candidateFieldIndices);
        }

        if (!candidateFieldIndices.length) {

            console.error('control has no matching field:', control);

            if (!this.threwMalformedToolbox) {

                this.threwMalformedToolbox = true;

                modal(ErrorLog, {
                    errors: [`This toolbox is malformed. The ${control.label} control has no compatible fields.`],
                    onClose: () => modal.close()
                });

            }

        }

        return candidateFieldIndices[0];

    }

    sharedAssetTypeClass(tool) {

        return this.sharedAssetTypes[tool.assetForm.assetType.assetTypeId] ? 'shared-asset-type' : '';

    }

    getLinkDescription(featureType, control) {

        return this.linkDescriptions[featureType.attributes.interface][control.controlTypeId];

    }

    // The only thing we use the "interpolate" expression for is geosizing values,
    // so if we see it then we know we're dealing with something geosized.
    isGeoSizedExpression(value) {
        return value && value[0] === 'interpolate';
    }

    reset() {

        this.toolbox = {
            attributes: {},
            group: {
                groups: []
            }
        };

        this.isUnlocked = false;

        this.isBase = false;

        this.saving = false;

        this.loading = false;

        this.lastSavedState = {};

        this.sharedColumns = {};

        this.invalidSharedColumns = {};

        this.projectToolCount = 0;

        document.title = 'Admin Dashboard | Unearth';

    }

    handleCoordinate({e, index, point, control, field}) {

        const value = Number(e.target.value);

        const isValid = !isNaN(value)
            && (index === 0 && value >= -180 && value <= 180
                || index === 1 && value >= -90 && value <= 90);

        e.target.parentNode.classList[isValid ? 'remove' : 'add']('error');

        point.coordinates[index] = e.target.value;

        if (isValid) {
            this.setDefault(control, field, point);
        }

    }

    removeControl(linkedOrStyled, featureType, controlLabel) {
        const linkedOrStyledControls = linkedOrStyled + 'Controls';
        if (controlLabel && featureType) {
            featureType.attributes[linkedOrStyledControls] = featureType.attributes[linkedOrStyledControls] || [];
            const index = featureType.attributes[linkedOrStyledControls].indexOf(controlLabel);
            if (index !== -1) {
                featureType.attributes[linkedOrStyledControls].splice(index, 1);
            }
        }
    }

    // Set the order in which features should be layered on the map,
    // from bottom to top, at toolbox.attributes.toolZOrder.
    // We don't currently provide a way for users to override this,
    // but they've asked for it.
    updateZOrder() {

        const polygons = [],
            polylines = [],
            symbols = [],
            markers = [],
            text = [],
            hidden = [],
            unmapped = []
            ;

        this.toolbox.group.groups.forEach(group => {

            group.tools.forEach(tool => {

                if (tool.featureStyles[0] && !tool.featureStyles[0].style) {

                    unmapped.push(tool.name);

                    return;

                }

                tool.attributes.styleZOrder = tool.featureStyles.map(featureStyle => featureStyle.name);

                const featureType = tool.featureTypes[0];

                if (featureType && !tool.attributes.hidden) {

                    switch (featureType.attributes.interface) {
                    case 'filepicker':
                        markers.push(tool.name);
                        break;
                    case 'polygon':
                        polygons.push(tool.name);
                        break;
                    case 'polyline':
                        polylines.push(tool.name);
                        break;
                    case 'symbol':
                        symbols.push(tool.name);
                        break;
                    case 'text':
                        text.push(tool.name);
                        break;
                    default:
                        hidden.push(tool.name);
                    }

                }

            });

        });

        this.toolbox.attributes.toolZOrder = [...polygons, ...polylines, ...symbols, ...text, ...markers, ...hidden, ...unmapped];

    }

    // Set toolbox.attributes.toolGroupOrder, which is the order that tool groups
    // should be rendered in the toolbox UI itself, as well as toolGroup.attributes.toolOrder,
    // which is the order that the tools should be rendered within the group.
    updateUIOrder() {

        this.toolbox.attributes.toolGroupOrder = this.toolbox.group.groups.map(group => {

            delete group.uiOrder;

            group.attributes.toolOrder = group.tools
                .filter(tool => {
                    delete tool.uiOrder;
                    return !tool.attributes.hidden;
                })
                .map(tool => tool.name);

            return group.name;

        });

    }

    // items is a list of objects.
    // returns true if more than one of those objects
    // have a "name" property with the same value.
    findDuplicateName(items, name = 'fieldName') {

        const names = {};

        let found;

        items.find(item => {
            if (names[item[name]] !== undefined) {
                found = item[name];
                return true;
            }
            names[item[name]] = true;
            return false;
        });

        return found;

    }

    addCommentTool() {
        const toolName = 'Comment';
        this.toolbox.group.groups[0].tools.push({
            toolId: UE.randomId(),
            name: toolName,
            attributes: {
                hidden: true,
                styleZOrder: []
            },
            assetForm: {
                assetFormId: UE.randomId(),
                name: toolName,
                attributes: {},
                controls: [
                    {
                        name: toolName,
                        label: toolName,
                        fieldName: toolName,
                        attributes: {},
                        controlTypeId: controlTypeNameToId.paragraph
                    }
                ],
                layout: {},
                assetType: {
                    assetTypeId: UE.randomId(),
                    name: toolName,
                    attributes: {
                        icon: {
                            mediaId: 'N0Y2tl4qjtCV-gP-Yf8M'
                        }
                    },
                    constraints: [],
                    fields: [{
                        name: toolName,
                        type: 'paragraph',
                        index: true
                    }]
                }
            },
            featureStyles: [],
            featureTypes: []
        });
    }

    addAttachmentTool(toolName) {
        const toolGroup = this.toolbox.group.groups.find(group => group.name === 'Files') || this.createToolGroup('Files');
        const mediaId = {
            Image: 'O24zWY6hxdWavlQp-Wxp',
            Audio: 'sL8gqkzyqAb2Nsr1ZlzN',
            Video: 'S1_daZSV05QD5_3WIlOQ',
            File: 'jOhb0riXOSiMSvpiN_oK'
        }[toolName];
        const featureTypeId = UE.randomId();
        toolGroup.tools.push({
            toolId: UE.randomId(),
            name: toolName,
            attributes: {
                styleZOrder: [toolName]
            },
            assetForm: {
                assetFormId: UE.randomId(),
                name: toolName,
                attributes: {},
                controls: [
                    {
                        name: 'Name',
                        label: 'Name',
                        controlTypeId: controlTypeNameToId.name,
                        fieldName: 'Name',
                        attributes: {}
                    },
                    {
                        name: toolName,
                        label: toolName,
                        controlTypeId: controlTypeNameToId.file,
                        fieldName: toolName,
                        attributes: {
                            accept: {
                                Image: ['image/*'],
                                Audio: ['audio/*'],
                                Video: ['video/*']
                            }[toolName],
                            maxFiles: 1,
                            minFiles: 1
                        }
                    },
                    {
                        name: 'Description',
                        label: 'Description',
                        controlTypeId: controlTypeNameToId.paragraph,
                        fieldName: 'Description',
                        attributes: {}
                    },
                    {
                        name: 'Coordinates',
                        label: 'Coordinates',
                        controlTypeId: controlTypeNameToId.coordinates,
                        fieldName: 'Coordinates',
                        attributes: {}
                    },
                    {
                        name: 'Comments',
                        label: 'Comments',
                        controlTypeId: controlTypeNameToId.comments,
                        fieldName: 'Comments',
                        attributes: {}
                    },
                    {
                        name: 'Attachments',
                        label: 'Attachments',
                        controlTypeId: controlTypeNameToId.links,
                        fieldName: 'Attachments',
                        attributes: {}
                    }
                ],
                layout: {},
                assetType: {
                    assetTypeId: UE.randomId(),
                    name: toolName,
                    attributes: {
                        icon: {mediaId}
                    },
                    constraints: [],
                    fields: [
                        {
                            name: 'Comments',
                            type: 'comments'
                        },
                        {
                            name: 'Attachments',
                            type: 'attachments'
                        },
                        {
                            name: toolName,
                            type: {
                                type: 'array',
                                items: {
                                    type: 'objRef',
                                    objType: 'media'
                                }
                            }
                        },
                        {
                            name: 'Description',
                            type: 'paragraph',
                            index: true
                        },
                        {
                            name: 'Name',
                            type: 'name',
                            index: true
                        },
                        {
                            name: 'Coordinates',
                            type: {
                                type: 'geometry',
                                geomType: 'Point'
                            }
                        }
                    ]
                }
            },
            featureStyles: [{
                attributes: {},
                style: {
                    type: 'symbol',
                    layout: {
                        'icon-size': [
                            '/', 32,
                            ['get', '_sourceWidthPx']
                        ],
                        'icon-allow-overlap': true,
                        'icon-anchor': 'bottom',
                        'icon-image': {
                            Image: 'UlvwdTGghQ0syQopfcqJ',
                            Audio: 'KpqXixsNk27-_sGsp3JP',
                            Video: 'b819NIukIxYWAZyG8YqM',
                            File: 'x-eHR9Q1nLl1OWQMLq2t'
                        }[toolName]
                    }
                },
                featureStyleId: UE.randomId(),
                name: 'Image',
                featureType: {featureTypeId}
            }],
            featureTypes: [{
                featureTypeId,
                name: toolName,
                geometry: {
                    type: 'Point'
                },
                attributes: {
                    interface: 'filepicker',
                    command: 'place',
                    linkedControls: [toolName, 'Name', 'Coordinates'],
                    icon: {mediaId}
                },
                properties: {}
            }]
        });
    }

    showWarningModal({headline, message, onYes, onNo, yesText, noText}) {
        modal({
            view: () => <div class="unlock-warning-modal">
                <div class="unlock-warning-text">
                    <h2>{headline ? headline : '⚠️ Warning ⚠️'}</h2>
                    {message}
                </div>
                <div class="btn-pair">
                    <span class="btn btn-error" onclick={() => onYes()}>{yesText}</span>
                    <span class="btn btn-primary" onclick={() => onNo()}>{noText}</span>
                </div>
            </div>
        });
    }

    // When this.isUnlocked is true,
    // backwards-incompatible toolbox edits will be permitted.
    unlock() {
        this.showWarningModal({
            message: <><p style="margin-top: 40px;">Once unlocked, backwards-incompatible toolkit edits <br />will be permitted,
                increasing the risk of breaking changes.</p>
            <p style="margin-top: 40px;">Are you sure you want to proceed?</p></>,
            onYes: () => {
                this.isUnlocked = true;
                modal.close();
            },
            onNo: () => modal.close(),
            yesText: 'Yes, the risk is worth the reward.',
            noText: 'No, please take me back!'
        });
    }

    missingRequiredTool(toolName) {
        return `This toolbox has no ${toolName} tool, and is therefore irrecoverably corrupted. Do not attempt to fix it.`;
    }

    findMissingTools(tools, errors) {
        if (!tools.find(tool => tool.name === 'Comment')) {
            if (this.isBase) {
                this.addCommentTool();
            } else {
                errors.push(this.missingRequiredTool('Comment'));
            }
        }

        if (!tools.find(tool => tool.name === 'Image')) {
            if (this.isBase) {
                this.addAttachmentTool('Image');
            } else {
                errors.push(this.missingRequiredTool('Image'));
            }
        }

        if (!tools.find(tool => tool.name === 'Audio')) {
            if (this.isBase) {
                this.addAttachmentTool('Audio');
            } else {
                errors.push(this.missingRequiredTool('Audio'));
            }
        }

        if (!tools.find(tool => tool.name === 'Video')) {
            if (this.isBase) {
                this.addAttachmentTool('Video');
            } else {
                errors.push(this.missingRequiredTool('Video'));
            }
        }

        if (!tools.find(tool => tool.name === 'File')) {
            if (this.isBase) {
                this.addAttachmentTool('File');
            } else {
                errors.push(this.missingRequiredTool('File'));
            }
        }

    }

    // This is where we enforce a variety of rules for toolbox configurations.
    // Many of these are not hard rules in order for a toolbox to work, but they're
    // sanity checks to help put guardrails up that will help prevent confusion.
    findErrors(onSuccess, onFailure) {

        const errors = [];

        try {

            Array.from(document.querySelectorAll('.thumbnail')).forEach(img => {

                if (img.naturalWidth > 256) {

                    errors.push(<p>Icon is too large: <img src={img.src} width="36" style="margin-bottom: -12px" /></p>);

                }

            });

            if (!this.toolbox.name) {
                errors.push('Missing toolbox name.');
            }

            Object.keys(this.sharedColumns).forEach(sharedColumn => {
                if (!this.sharedColumnGroupIsValid(sharedColumn)) {
                    errors.push(`Shared column for control name "${sharedColumn}" has conflicting controls.`);
                }
            });

            if (this.isMetaProject()) {
                this.toolbox.attributes.isMetaToolbox = true;
            } else {
                delete this.toolbox.attributes.isMetaToolbox;
            }

            this.toolbox.group.name = 'top';

            const customExports = this.toolbox.attributes.customExports;

            if (customExports) {
                customExports.forEach((exp, i) => {
                    if (!exp.name && !exp.id) {
                        customExports.splice(i, 1);
                    } else if (!exp.name || !exp.id) {
                        errors.push('Each custom export must have both a name and an ID.');
                    }
                });
                const duplicateExportName = this.findDuplicateName(customExports);
                if (duplicateExportName !== undefined) {
                    errors.push(`Custom export name "${duplicateExportName}" is not unique.`);
                }
                if (!customExports.length) {
                    delete this.toolbox.attributes.customExports;
                }
            }
            if (this.toolbox.attributes.featureFlags && this.isMetaProject()) {
                const progressDash = this.toolbox.attributes.featureFlags[PROGRESS_DASHBOARD_FLAG];

                if (progressDash) {
                    if (!this.toolbox.attributes.progressDashID) {
                        errors.push('Progress Dashboard must have an ID.');
                    }
                }
            }
            if (this.toolbox.attributes.projectSingular && !this.toolbox.attributes.projectPlural) {
                errors.push('Toolbox has a singular custom "project" term but no plural term.');
            }

            if (!this.toolbox.attributes.projectSingular && this.toolbox.attributes.projectPlural) {
                errors.push('Toolbox has a plural custom "projects" term but no singular term.');
            }

            const tools = [];
            this.toolbox.group.groups.forEach(toolGroup => tools.push(...toolGroup.tools));

            if (!this.toolbox.attributes.isMetaToolbox) {
                this.findMissingTools(tools, errors);
            }

            if (!(this.toolbox.attributes.icon && this.toolbox.attributes.icon.mediaId)) {
                errors.push('No toolbox icon found.');
            }

            if (this.toolbox.group.groups.find(toolGroup => !toolGroup.name)) {
                errors.push('One or more tool groups have no name.');
            }

            const duplicateGroupName = this.findDuplicateName(this.toolbox.group.groups);
            if (duplicateGroupName !== undefined) {
                errors.push(`Tool group name "${duplicateGroupName}" is not unique.`);
            }

            if (tools.find(tool => !tool.name)) {
                errors.push('One or more tools have no name.');
            }

            const duplicateToolName = this.findDuplicateName(tools);
            if (duplicateToolName !== undefined) {
                errors.push(`Tool name "${duplicateToolName}" is not unique.`);
            }

            tools.forEach(tool => {

                const fields = tool.assetForm.assetType.fields;

                const icon = tool.assetForm.assetType.attributes.icon;
                if (!(icon && icon.mediaId) && !tool.attributes.hidden) {
                    errors.push(`The ${tool.name} tool is missing an icon.`);
                }

                permissionsModel.removeIfSetToDefault(tool);

                if (!tool.assetForm.controls.find(control => control.controlTypeId === controlTypeNameToId.comments)) {
                    tool.assetForm.controls.push({
                        name: 'Comments',
                        label: 'Comments',
                        controlTypeId: controlTypeNameToId.comments,
                        fieldName: 'Comments',
                        attributes: {}
                    });
                }

                if (fields && !fields.find(field => field.type === 'comments')) {
                    fields.push({
                        name: 'Comments',
                        type: 'comments'
                    });
                }

                if (!tool.assetForm.controls.find(control => control.controlTypeId === controlTypeNameToId.links)) {
                    tool.assetForm.controls.push({
                        name: 'Attachments',
                        label: 'Attachments',
                        controlTypeId: controlTypeNameToId.links,
                        fieldName: 'Attachments',
                        attributes: {}
                    });
                }

                if (fields && !fields.find(field => field.type === 'attachments')) {
                    fields.push({
                        name: 'Attachments',
                        type: 'attachments'
                    });
                }

                const styleFeature = tool.featureStyles && tool.featureStyles[0];
                if (styleFeature && styleFeature.style) {
                    if (styleFeature.style.type === 'line') {
                        this.removeDupeToolStyleNames(tool);
                    }
                }

                const duplicateStyleName = this.findDuplicateName(tool.featureStyles);
                if (duplicateStyleName !== undefined) {
                    errors.push(`Style name "${duplicateStyleName}" is not unique within the ${tool.name} tool.`);
                }

                let duplicateControlLabel = this.findDuplicateName(tool.assetForm.controls, 'fieldName');
                if (duplicateControlLabel !== undefined) {
                    if (duplicateControlLabel === PROJECT_CONTROL_LABEL) {
                        duplicateControlLabel = 'Project';
                    }
                    errors.push(`Tool ${tool.name} has multiple "${duplicateControlLabel}" controls.`);
                }

                if (tool.assetForm.controls.filter(control => control.controlTypeId === controlTypeNameToId.name).length > 1) {
                    errors.push(`The ${tool.name} has multiple name controls.`);
                }

                let linksControl;

                const controls = tool.assetForm.controls;

                controls.forEach(control => {

                    control.attributes = control.attributes || {};

                    if (control.controlTypeId === controlTypeNameToId.links) {
                        linksControl = control;
                    }

                    const evaluation = control.attributes.eval;

                    if (evaluation) {
                        if (!this.isValidJson(evaluation.expression)) {
                            errors.push(`The ${control.label} control in the ${tool.name} tool has an invalid automated field expression.`);
                        }
                    }

                    if (control.controlTypeId === controlTypeNameToId.request) {
                        if (!control.attributes.url && !control.attributes.eval) {
                            errors.push(`The ${control.label} control in the ${tool.name} tool must have either a url or a automated field expression.`);
                        } else if (control.attributes.url && control.attributes.eval) {
                            errors.push(`The ${control.label} control in the ${tool.name} tool should not have both a url and a automated field expression.`);
                        }
                        if (!this.isValidJson(control.attributes.parse)) {
                            errors.push(`The ${control.label} control in the ${tool.name} tool has an invalid response parser expression.`);
                        }
                    }

                    if (control.controlTypeId === controlTypeNameToId.project) {
                        if (control.label !== PROJECT_CONTROL_LABEL) {
                            errors.push(`The ${control.label} project control in the ${tool.name} tool should not have a custom label.`);
                        }
                        control.attributes.hidden = true;
                    }

                    // https://unearth.atlassian.net/browse/WEB-2315
                    if (control.attributes.default === '') {
                        delete control.attributes.default;
                        if (!this.isEmbedControl(control.controlTypeId)) {
                            const matchingField = fields.find(field => field.name === control.fieldName);
                            if (matchingField) {
                                delete matchingField.default;
                            }
                        }
                    }
                    if (control.attributes.default) {
                        if (Array.isArray(control.attributes.default)) {
                            const values = [];
                            control.attributes.default.forEach(value => {
                                const trimmedValue = value.trim();
                                values.push(trimmedValue);
                            });
                            control.attributes.default = values;
                        }

                    }

                    if (control.controlTypeId === controlTypeNameToId.asset) {
                        const inputIds = control.attributes.acceptedAssetTypeIds;
                        if (inputIds && inputIds.length === 0) {
                            delete control.attributes.acceptedAssetTypeIds;
                        }
                    }
                });

                // Move links control to the bottom of the form
                if (linksControl) {
                    controls.splice(controls.indexOf(linksControl), 1);
                    controls.push(linksControl);
                }

                if (fields) {

                    tool.assetForm.controls.forEach(control => {
                        if (!this.isEmbedControl(control.controlTypeId)) {
                            const matchingField = fields.find(field => field.name === control.fieldName);
                            if (!matchingField) {
                                errors.push(`The ${control.label} control in the ${tool.name} tool has no matching field.`);
                            }
                        }
                    });


                    fields.forEach(field => {
                        if (field.default) {
                            if (Array.isArray(field.default)) {
                                const values = [];
                                field.default.forEach(value => {
                                    const trimmedValue = value.trim();
                                    values.push(trimmedValue);
                                });
                                field.default = values;
                            }
                        }
                        if (typeof field.type === 'object') {
                            const values = [];
                            if (field.type.type === 'array') {
                                if (field.type.items.type === 'enum') {
                                    field.type.items.values.forEach(value => {
                                        const trimmedValue = value.trim();
                                        values.push(trimmedValue);
                                    });
                                    field.type.items.values = values;
                                }
                            }
                            if (field.type.type === 'enum') {
                                field.type.values.forEach(value => {
                                    const trimmedValue = value.trim();
                                    values.push(trimmedValue);
                                });
                                field.type.values = values;
                            }
                        }

                        if (!tool.assetForm.controls.find(control => control.fieldName === field.name)) {
                            errors.push(`The ${field.name} field in the ${tool.name} tool has no matching control.`);
                        }
                    });
                }


                tool.featureStyles.forEach(featureStyle => {

                    let hasMedia;

                    if (featureStyle.style) {

                        if (featureStyle.style.paint && !Object.keys(featureStyle.style.paint).length) {
                            delete featureStyle.style.paint;
                        }

                        if (featureStyle.style.layout && !Object.keys(featureStyle.style.layout).length) {
                            delete featureStyle.style.layout;
                        }

                        if (!featureStyle.style.paint && !featureStyle.style.layout) {
                            errors.push(`Feature style ${featureStyle.name} of tool ${tool.name} is blank.`);
                        }

                        if (featureStyle.style.type === 'symbol' && featureStyle.style.layout['icon-size']) {

                            const iconImage = featureStyle.style.layout['icon-image'];

                            if (iconImage) {

                                featureStyle.attributes = featureStyle.attributes || {};

                                if (typeof iconImage === 'string') {
                                    hasMedia = true;
                                    featureStyle.attributes.mediaIds = [iconImage];
                                } else if (iconImage[0] === 'match') {
                                    hasMedia = true;
                                    featureStyle.attributes.mediaIds = iconImage
                                        .slice(3)
                                        .filter((item, k) => k % 2 === 0);
                                    const defaultMediaId = iconImage[iconImage.length - 1];
                                    if (!featureStyle.attributes.mediaIds.includes(defaultMediaId)) { // eslint-disable-line
                                        featureStyle.attributes.mediaIds.push(defaultMediaId);
                                    }
                                }

                            } else {

                                errors.push(`Feature style ${featureStyle.name} of tool ${tool.name} has no Image.`);

                            }

                        }

                    }

                    if (!hasMedia && featureStyle.attributes) {
                        delete featureStyle.attributes.mediaIds;
                    }

                    if (!tool.featureTypes.find(featureType => featureType.featureTypeId === featureStyle.featureType.featureTypeId)) {
                        errors.push(`No matching feature type found for style ${featureStyle.name} in tool ${tool.name}.`);
                    }

                });

                if (tool.featureTypes.length > 1) {
                    errors.push(`Tool ${tool.name} has multiple feature types.`);
                }

                const featureType = tool.featureTypes[0];

                if (featureType) {
                    if (featureType.attributes.styledControls && !featureType.attributes.styledControls.length) {
                        delete featureType.attributes.styledControls;
                    }
                    if (featureType.attributes.linkedControls && !featureType.attributes.linkedControls.length) {
                        delete featureType.attributes.linkedControls;
                    }
                    if (featureType.attributes.interface === 'project' && !controls.find(c => c.controlTypeId === controlTypeNameToId.project)) {
                        errors.push(`Tool ${tool.name} is missing a control of type "project."`);
                    }
                }

            });

        } catch (err) {

            console.error(err);

            errors.push(`Uncaught error: ${err.message}`);

        }

        if (errors.length) {

            onFailure(errors);

        } else {

            this.updateZOrder();

            this.updateUIOrder();

            onSuccess();

        }

    }

    validate(onSuccess, onFailure) {
        this.findErrors(onSuccess, onFailure);

    }

    findDiffBetweenTwoControls(controlA, controlB) {
        // Check control type:
        if (controlA.controlTypeId !== controlB.controlTypeId) {
            return true;
        }
        // Check name/label/fieldname:
        if (controlA.name !== controlB.name) {
            return true;
        }
        return false;
    }

    // Determine whether two fields are identical
    findDiffBetweenTwoAssetTypeFields(fieldA, fieldB) {
        if (fieldA.name !== fieldB.name) {
            return true;
        }
        // If it's a string type, compare it:
        if (typeof fieldA.type !== 'object' && fieldA.type !== fieldB.type) {
            return true;
        } else if (fieldA.type.type !== fieldB.type.type) {
            // If it's a type object, compare the next level type string:
            return true;
        }
        if (fieldA.type.type === 'enum') {
            const valuesA = fieldA.type.values || [];
            const valuesB = fieldB.type.values || [];

            if (valuesA.length !== valuesB.length) {
                return true;
            }
            let missingValue = false;
            valuesA.forEach(val => {
                if (!valuesB.find(value => value === val)) {
                    missingValue = true;
                }
            });
            if (missingValue) {
                return true;
            }
        }

        return false;
    }

    // Compare all shared columns in all groups
    sharedColumnGroupIsValid(sharedColumn) {
        if (!this.sharedColumns[sharedColumn]) {
            return true; // Not a shared column anymore, true by default
        }
        const toolIds = Object.keys(this.sharedColumns[sharedColumn]);
        if (toolIds.length === 1) {
            // only one tool using this tag, nothing to compare to
            return true;
        }
        const controlA = this.sharedColumns[sharedColumn][toolIds[0]].control; // start with the first control we find in the list of tools
        const fieldA = this.sharedColumns[sharedColumn][toolIds[0]].field;
        for (let i = 1; i < toolIds.length; i++) {
            const controlB = this.sharedColumns[sharedColumn][toolIds[i]].control;
            if (this.findDiffBetweenTwoControls(controlA, controlB)) {
                return false;
            }
            const fieldB = this.sharedColumns[sharedColumn][toolIds[i]].field;
            if (this.findDiffBetweenTwoAssetTypeFields(fieldA, fieldB)) {
                return false;
            }
        }
        return true;
    }

    removeControlFromSharedColumns(tool, control) {
        if (control.attributes.sharedColumn) {
            delete this.sharedColumns[control.name][tool.id];
            this.revalidateSharedColumn(control.name);
        }
        m.redraw();
    }

    revalidateSharedColumn(sharedColumn) {
        if (!this.sharedColumnGroupIsValid(sharedColumn)) {
            this.invalidSharedColumns[sharedColumn] = true;
        } else {
            delete this.invalidSharedColumns[sharedColumn];
        }
        m.redraw();
    }

    // Compare one new shared control to the existing shared column
    sharedColumnIsValid(control, field) {
        if (!this.sharedColumns.hasOwnProperty(control.name)) {
            return true;  // First shared column for this control name — defaults to valid.
        }
        const testingAgainst = Object.values(this.sharedColumns[control.name])[0];
        const testControl = testingAgainst.control;
        const testField = testingAgainst.field;
        // Compare test control against this control
        if (this.findDiffBetweenTwoControls(testControl, control)) {
            return false;
        }
        if (this.findDiffBetweenTwoAssetTypeFields(testField, field)) {
            return false;
        }
        return true;
    }

    removeFromSharedColumns(control, toolId) {
        const tag = control.name;
        const key = `${toolId}${control.name}`; // Unique to control and asset type
        if (this.sharedColumns[tag]) {
            delete this.sharedColumns[tag][key];
            // If it was the control using that tag, remove the tag from the list:
            if (!Object.keys(this.sharedColumns[tag]).length) {
                delete this.sharedColumns[tag];
            }
        }
        this.revalidateSharedColumn(tag);

        m.redraw();
    }

    addSharedColumnTag(control, field, toolId) {
        const tag = control.name;
        this.sharedColumns[tag] = this.sharedColumns[tag] || {};
        const key = `${toolId}${control.name}`; // Unique to control and asset type
        this.sharedColumns[tag][key] = {control, field};

        this.revalidateSharedColumn(tag);
    }

    // This converts external ID keys like "toolboxId"
    // into the newer shorthand, "xid"
    // and also optionally strips created/updatedDateTime,
    // which cannot be present on records sent to the API
    stripDateAndID(record, type, doStripDates) {

        const idKey = type + 'Id';

        record.xid = record[idKey] || UE.randomId();

        delete record[idKey];

        if (doStripDates) {
            delete record.createdDateTime;
            delete record.updatedDateTime;
        }

        return record;

    }

    // The api sends and receives toolboxes using different schemas.
    // This converts a toolbox from what we received from the API
    // into something we can send to the API.
    getToolboxDescriptor(toolbox, doStripDates) {

        const isNew = !toolbox.createdDateTime;

        toolbox = deepCloneObject(toolbox);

        delete toolbox.account;
        delete toolbox.accountId;

        this.stripDateAndID(toolbox, 'toolbox', doStripDates);

        this.stripDateAndID(toolbox.group, 'toolGroup', doStripDates);

        toolbox.group.groups.forEach(toolGroup => {

            this.stripDateAndID(toolGroup, 'toolGroup', doStripDates);

            toolGroup.tools.forEach(tool => {

                tool.assetForm.assetType = {xid: tool.assetForm.assetType.assetTypeId};

                this.stripDateAndID(tool, 'tool', doStripDates);

                this.stripDateAndID(tool.assetForm, 'assetForm', doStripDates);

                const featureType = tool.featureTypes[0],
                    featureTypeId = featureType && featureType.featureTypeId;

                if (featureType) {

                    tool.featureTypes = [{xid: featureTypeId}];

                }


                tool.featureStyles.forEach(featureStyle => {

                    if (featureType) {

                        featureStyle.featureType = {xid: featureTypeId};

                    }

                    this.stripDateAndID(featureStyle, 'featureStyle', doStripDates);

                });


            });

        });

        return {
            accountId: isNew ? this.getAccountId() : undefined,
            descriptors: {
                toolboxes: [toolbox]
            }
        };

    }

    // Converts records from the schema that can be sent to the API
    // into the schema that we receive from the API
    processDescriptors(toolboxDescriptor, featureTypesDescriptor, assetTypesDescriptor) {

        const toolbox = toolboxDescriptor.toolboxes[0],
            assetTypes = {},
            featureTypes = {};

        featureTypesDescriptor.featureTypes.forEach(featureType => {

            featureType.featureTypeId = featureType.xid;

            featureTypes[featureType.xid] = featureType;

        });

        assetTypesDescriptor.assetTypes.forEach(assetType => {

            assetType.assetTypeId = assetType.xid;

            assetTypes[assetType.xid] = assetType;

        });

        toolbox.toolboxId = toolbox.xid;

        toolbox.group.toolGroupId = toolbox.group.xid;

        toolbox.group.groups.forEach(toolGroup => {

            toolGroup.toolGroupId = toolGroup.xid;

            toolGroup.tools.forEach(tool => {

                tool.toolId = tool.xid;

                tool.assetForm.assetFormId = tool.assetForm.xid;

                tool.assetForm.assetType = assetTypes[tool.assetForm.assetType.xid];

                if (tool.featureTypes) {

                    tool.featureStyles.forEach(featureStyle => {

                        featureStyle.featureStyleId = featureStyle.xid;

                        featureStyle.featureType.featureTypeId = featureStyle.featureType.xid;

                    });

                }

                tool.featureTypes.forEach(featureType => {

                    Object.assign(featureType, featureTypes[featureType.xid]);

                });

            });

        });

        return toolbox;

    }

    // Converts asset types or feature types from the schema
    // that the API sends into the schema that the API can receive
    getTypeDescriptors(records, recordType, doStripDates, includeAccountId) {
        return {
            accountId: includeAccountId ? this.getAccountId() : undefined,
            descriptors: {
                [recordType + 's']: records.map(record => {
                    record = deepCloneObject(record);
                    delete record.account;
                    delete record.accountId;
                    delete record.isVisible;
                    delete record.creator;
                    // delete record.
                    return this.stripDateAndID(record, recordType, doStripDates);
                })
            }
        };
    }

    // Split asset types and feature types into those
    // which need to be created and those which need to be modified
    sortDescriptors() {
        const newAssetTypes = [];
        const oldAssetTypes = [];
        const newFeatureTypes = [];
        const oldFeatureTypes = [];
        this.toolbox.group.groups.forEach(group =>
            group.tools.forEach(tool => {
                const assetType = tool.assetForm.assetType;
                if (assetType.createdDateTime) {
                    oldAssetTypes.push(assetType);
                } else {
                    newAssetTypes.push(assetType);
                }
                tool.featureTypes.forEach(featureType => {
                    if (featureType.createdDateTime) {
                        oldFeatureTypes.push(featureType);
                    } else {
                        newFeatureTypes.push(featureType);
                    }
                });
            })
        );
        return [newAssetTypes, oldAssetTypes, newFeatureTypes, oldFeatureTypes];
    }

    // Validates the toolbox, creates all new records, modifies all existing records
    async publish() {

        this.saving = true;

        const quitEarly = await this.showCustomReporterWarning(true);
        if (quitEarly) {
            return;
        }

        this.validate(() => {

            delete this.toolbox.savedDateTime;
            const [newAssetTypes, oldAssetTypes, newFeatureTypes, oldFeatureTypes] = this.sortDescriptors();

            const rpc = [];

            if (newAssetTypes.length) {
                rpc.push(['createAssetTypes', this.getTypeDescriptors(newAssetTypes, 'assetType', true, true)]);
            }
            if (oldAssetTypes.length) {
                rpc.push(['modifyAssetTypes', this.getTypeDescriptors(oldAssetTypes, 'assetType', true, false)]);
            }
            if (newFeatureTypes.length) {
                rpc.push(['createFeatureTypes', this.getTypeDescriptors(newFeatureTypes, 'featureType', true, true)]);
            }
            if (oldFeatureTypes.length) {
                rpc.push(['modifyFeatureTypes', this.getTypeDescriptors(oldFeatureTypes, 'featureType', true, false)]);
            }

            const toolboxVerb = this.toolbox.createdDateTime ? 'modify' : 'create';

            const toolboxDescriptor = this.getToolboxDescriptor(this.toolbox, true);

            UE.rpc(rpc).then(() => UE.rpc([
                [toolboxVerb + 'Toolboxes', toolboxDescriptor]
            ])).then(() => {
                this.isUnlocked = false;
                modal({
                    view: () => <div style={'text-align: center;'}><h3>Toolbox published successfully! ✅</h3></div>
                }, {onClose: () => {
                    window.location.reload();
                }});
                draftsModel.deleteDraft(this.toolbox.toolboxId);
                m.redraw();
            }).catch((e) => {
                console.error(e);
                let errorMessage = '';
                try {
                    const error = JSON.parse(e.payload.results[0].message);
                    errorMessage = error.value ? `Invalid character found in ${error.value}` : 'Tool is missing a Name';
                } catch (err) {
                    errorMessage = 'Publish failed. More details in the console.';
                }

                return modal(ErrorLog, {
                    errors: [errorMessage],
                    onClose: () => modal.close()
                });
            }).finally(() => {
                this.saving = false;
                m.redraw();
            });

        }, errors => {

            console.error(errors);

            modal(ErrorLog, {
                errors,
                onClose: () => modal.close()
            });

            this.saving = false;

        });

    }

    downloadJSON(json, title) {

        const blob = new Blob([JSON.stringify(json, null, 4)], {type: 'application/json'}),
            url = URL.createObjectURL(blob),
            a = document.createElement('a');

        a.download = title;
        a.href = url;

        a.click();

    }

    getAccountId() {
        const toolbox = this.pendingToolbox || this.toolbox;
        return toolbox.account && toolbox.account.accountId || toolbox.accountId || router.params.accountId || constants.unearthAccountId;
    }

    export() {

        const allAssetTypes = [],
            allFeatureTypes = [];

        this.toolbox.group.groups.forEach(group =>
            group.tools.forEach(tool => {
                allAssetTypes.push(tool.assetForm.assetType);
                allFeatureTypes.push(...tool.featureTypes);
            })
        );

        const toolboxDescriptor = this.getToolboxDescriptor(this.toolbox, false),
            assetTypeDescriptors = this.getTypeDescriptors(allAssetTypes, 'assetType', false),
            featureTypeDescriptors = this.getTypeDescriptors(allFeatureTypes, 'featureType', false),
            name = this.toolbox.name.toLowerCase().replace(/ /g, '-');

        toolboxDescriptor.descriptors.toolboxes[0].account = {accountId: this.getAccountId()};

        this.downloadJSON(featureTypeDescriptors, name + '-feature-types.json');
        this.downloadJSON(assetTypeDescriptors, name + '-asset-types.json');
        this.downloadJSON(toolboxDescriptor, name + '-toolbox.json');

    }

    readJSONFile(file) {

        return new Promise((resolve, reject) => {

            const reader = new FileReader();

            reader.onload = e => {

                try {

                    resolve(JSON.parse(e.target.result));

                } catch (err) {

                    reject(file.name + ' is not a valid JSON file.');

                }

            };

            reader.readAsText(file);

        });

    }

    import() {
        let doClone;
        modal({
            view: () =>
                <div class="clone-dialog">
                    <h2>Import Toolbox</h2>
                    <p>What would you like to do with the imported toolbox?</p>
                    <p>
                        <input type="radio" name="import" id="import-resume" checked={!doClone} oninput={() => {
                            doClone = false;
                        }} />
                        <label for="import-resume">Resume work on this toolbox</label>
                    </p>
                    <p>
                        <input type="radio" name="import" id="import-clone" checked={doClone} oninput={() => {
                            doClone = true;
                        }} />
                        <label for="import-clone">Create a new copy of this toolbox</label>
                    </p>
                    <div className="horizontal-align">
                        <MreactComponent element={ButtonElement({
                            handleButtonClick: () => modal.close(),
                            buttonText: 'Cancel',
                            buttonSize: 'default',
                            buttonColor: 'white'
                        })} />
                        <MreactComponent element={ButtonElement({
                            handleButtonClick: () => {
                                modal.close();
                                this._import(doClone);
                            },
                            buttonText: 'Select toolbox files',
                            buttonSize: 'default',
                            buttonColor: 'default'
                        })} />
                    </div>

                </div>
        });
    }

    _import(doClone) {

        uploadModel.pickFiles({
            type: 'application/json',
            multiple: true
        }).then(files => {

            Promise.all(files.map(file => this.readJSONFile(file)))
                .then(data => {

                    const toolbox = data.find(d => d.descriptors.toolboxes),
                        featureTypes = data.find(d => d.descriptors.featureTypes),
                        assetTypes = data.find(d => d.descriptors.assetTypes);

                    if (!(toolbox
                        && featureTypes
                        && assetTypes)) {

                        throw new Error('Toolbox imports require a toolbox file, a feature types file, and an asset types file.');

                    }

                    this.pendingToolbox = this.processDescriptors(toolbox.descriptors, featureTypes.descriptors, assetTypes.descriptors);

                    if (doClone) {

                        this.pendingToolbox = this.cloneToolbox(this.pendingToolbox);

                    } else if (this.toolbox && this.toolbox.updatedDateTime && this.toolbox.toolboxId !== this.pendingToolbox.toolboxId) {

                        throw new Error('You can\'t import a toolbox into another toolbox that has already been published.');

                    }

                    router.merge({
                        view: 'toolbox',
                        accountId: this.getAccountId(),
                        toolboxId: this.pendingToolbox.toolboxId
                    });

                }).catch(err => {

                    modal(ErrorLog, {
                        errors: Array.isArray(err) ? err : [err.message || err],
                        onClose: () => modal.close()
                    });

                });

        });

    }

    // create a new, blank toolbox
    create() {

        const accountId = constants.unearthAccountId;

        this.pendingToolbox = {
            toolboxId: UE.randomId(),
            attributes: {},
            group: {
                toolGroupId: UE.randomId(),
                groups: []
            },
            account: {accountId}
        };

        router.merge({
            view: 'toolbox',
            accountId,
            toolboxId: this.pendingToolbox.toolboxId
        });

    }

    preview() {

        this.validate(() => {

            toolboxPreview(this.toolbox);

        }, errors => {

            modal(ErrorLog, {
                errors,
                onClose: () => modal.close()
            });

        });

    }

    // This method iterates an existing toolbox and looks for deprecated, corrupted, or
    // otherwise problematic configurations from which we can automatically recover.
    // When we encounter a bug that is caused by an unexpected toolbox configuration,
    // we often update this method to make sure it doesn't happen again in other toolboxes.
    normalize(toolbox) {

        // lastSavedState is used to keep track of what edits have been made
        // since the toolbox was last published
        this.lastSavedState.toolGroups = {};

        this.isBase = !toolbox.base;

        this.projectToolCount = 0;

        toolbox.group.groups.forEach(toolGroup => {

            this.lastSavedState.toolGroups[toolGroup.toolGroupId] = toolGroup;

            toolGroup.tools.forEach(tool => {

                const featureType = tool.featureTypes && tool.featureTypes[0];

                if (featureType) {

                    featureType.attributes = featureType.attributes || {};
                    featureType.properties = featureType.properties || {};
                    featureType.name = featureType.name || tool.name;

                    if (!featureType.geometry) {
                        delete featureType.geometry;
                    }

                    if (featureType.attributes.interface === 'project') {
                        this.projectToolCount++;
                    }

                }

                this.lastSavedState[tool.toolId] = {};

                tool.assetForm.controls.forEach(control => {

                    this.lastSavedState[tool.toolId][control.fieldName] = control;

                    if (control.controlTypeId === controlTypeNameToId.project) {
                        const field = tool.assetForm.assetType.fields.find(f => f.name === control.fieldName);
                        control.attributes.hidden = true;
                        this.changeControlLabel(PROJECT_CONTROL_LABEL, control, field, featureType);
                        control.attributes.hidden = true;
                    }

                    if (control.attributes.sharedColumn) {
                        const field = tool.assetForm.assetType.fields.find(f => f.name === control.fieldName);
                        this.addSharedColumnTag(control, field, tool.toolId);
                    }

                });

                tool.assetForm.assetType.fields.forEach(field => {

                    // We had some inconsistent date field types.
                    // This corrects them.
                    if (field.type === 'date') {

                        field.type = 'datetime';

                        // We changed some PG&E address controls to "name"
                        // without updating the fields, and then cloned
                        // that tool a bunch of times. This fixes it.
                    } else if (field.type === 'text') {

                        const control = tool.assetForm.controls.find(c => c.label === field.name);

                        if (control && control.controlTypeId === controlTypeNameToId.name) {

                            field.type = 'name';

                        }

                    }

                });

                tool.featureStyles.forEach(featureStyle => {

                    ['paint', 'layout'].forEach(styleType => {

                        const style = featureStyle.style && featureStyle.style[styleType];

                        if (style) {

                            Object.keys(style).forEach(key => {

                                const oldExpression = style[key];

                                // Categorical expressions don't support our implementation of icon-size,
                                // so lets rewrite them all as 'match' expressions.
                                if (oldExpression.type === 'categorical') {

                                    const newExpression = ['match', ['get', oldExpression.property]];

                                    oldExpression.stops.forEach(stop => {

                                        newExpression.push(...stop);

                                    });

                                    newExpression.push(
                                        oldExpression.default === undefined
                                            ? newExpression[3]
                                            : oldExpression.default
                                    );

                                    style[key] = newExpression;

                                } else if (oldExpression.type === 'identity') {

                                    style[key] = ['get', oldExpression.property];

                                } else if (this.isBracketGetter(oldExpression)) {

                                    style[key] = ['get', this.getBracketProperty(oldExpression)];

                                }

                            });

                        }

                    });

                });

                // Ensure that labels appear on top of their respective features
                if (tool.featureStyles.length > 1) {
                    tool.featureStyles.sort((a, b) => {
                        if (a.style && a.style.layout && a.style.layout['text-field'] !== undefined) {
                            return 1;
                        }
                        if (b.style && b.style.layout && b.style.layout['text-field'] !== undefined) {
                            return -1;
                        }
                        return 0;
                    });
                }

            });

        });

        return toolbox;

    }

    reorder() {

        const toolGroupOrder = this.toolbox.attributes.toolGroupOrder || [];

        this.toolbox.group.groups.forEach(toolGroup => {

            const toolOrder = toolGroup.attributes.toolOrder || [];

            toolGroup.uiOrder = toolGroupOrder.indexOf(toolGroup.name);

            toolGroup.tools.forEach(tool => {

                tool.uiOrder = toolOrder.indexOf(tool.name);

            });

            toolGroup.tools.sort((a, b) => a.uiOrder - b.uiOrder);

        });

        this.toolbox.group.groups.sort((a, b) => a.uiOrder - b.uiOrder);

    }

    verifyIsBase(toolbox) {
        // Users have been accidentally editing variants instead of bases,
        // and there isn't really a use case for that without an external
        // toolbox editor, so let's redirect variants to their bases.
        if (toolbox.base) {

            router.mergeReplace({toolboxId: toolbox.base.toolboxId});

            return false;

        }

        return true;

    }

    fetch() {
        this.reset();

        this.loading = true;

        if (this.pendingToolbox) {

            if (this.verifyIsBase(this.pendingToolbox)) {

                this.toolbox = window.toolbox = this.normalize(this.pendingToolbox);

                this.reorder();

                delete this.pendingToolbox;

                router.url.mergeReplace({
                    toolboxId: this.toolbox.toolboxId,
                    accountId: this.getAccountId()
                });

                this.loading = false;

            }

        } else {

            UE.rpc([['getToolbox', {
                toolboxId: router.params.toolboxId
            }]]).then(([toolbox]) => this.loadToolbox(toolbox))
                .catch(() => draftsModel.retrieveDraft(router.params.toolboxId)
                    .then((_toolbox) => this.loadToolbox(deepCloneObject(_toolbox)))
                    .catch(_e => {
                        console.error(_e);

                        modal(ErrorLog, {
                            errors: ['Toolbox not found.'],
                            onClose: () => modal.close()
                        });
                    })

                );

        }

    }

    loadToolbox(toolbox) {
        if (this.verifyIsBase(toolbox)) {
            this.toolbox = window.toolbox = this.normalize(toolbox);
            if (this.isMetaProject() && !this.toolboxFeatures.includes(PROGRESS_DASHBOARD_FLAG)) {
                this.toolboxFeatures.push(PROGRESS_DASHBOARD_FLAG);
            }

            this.showCustomReporterWarning();

            this.reorder();

            router.url.mergeReplace({
                accountId: this.getAccountId()
            });

            document.title = `${this.toolbox.name || 'Unnamed'} | id: ${this.toolbox.toolboxId} | Unearth Toolbox`;

            this.loading = false;
            m.redraw();
        }

    }

    hasCustomReports() {
        return this.toolbox.attributes
            && this.toolbox.attributes.customExports
            && this.toolbox.attributes.customExports.length
            && this.toolbox.attributes.customExports[0].id
            && this.toolbox.attributes.customExports[0].id.length;
    }

    showCustomReporterWarning(isAboutToPublish = false) {
        if (this.hasCustomReports()) {
            const noText = isAboutToPublish ? 'Export my changes and open a jira ticket' : 'Never mind, I\'ll open a jira ticket.';
            const yesText = isAboutToPublish ? 'I accept the risk, publish anyway.' : 'I understand and want to continue.';
            return new Promise((resolve) => this.showWarningModal({
                message: <><p style="margin-top: 40px;">The toolkit you're about to change has a custom report.</p>
                    <p style="margin-top: 20px;">Please inform the Cloud Team by creating a JIRA ticket including the 'cloud' label and the toolkit changes you're making. </p><p style="margin-top: 20px;">Once you publish, if there are breaking changes to the custom report, it will no longer work.</p></>,
                onYes: () => {
                    modal.close();
                    return resolve(false);
                },
                onNo: () => {
                    if (isAboutToPublish) {
                        this.export();
                    }
                    window.location = 'https://unearth.atlassian.net/jira/secure/CreateIssueDetails!init.jspa?issuetype=10120&pid=10120&summary=Cloud%3A%20Changes%20needed%20on%20toolkit%20with%20custom%20report&labels=cloud&priority=2';
                    return resolve(true);
                },
                yesText,
                noText
            }));
        }
        return Promise.resolve();
    }

    changeControlLabel(value, control, field, featureType) {
        // controls of type "project" must use a special label: _projectId
        // this is an unfortunate hack that allows clients to filter out assets
        // that represent projects which the user does not have access to.
        // I promise we wouldn't have done it this way if we had any better options.
        if (control.controlTypeId === controlTypeNameToId.project) {
            control.attributes.hidden = true;
            value = PROJECT_CONTROL_LABEL;
        }
        if (featureType && featureType.attributes.linkedControls) {
            const linkedControlIndex = featureType.attributes.linkedControls.indexOf(control.label);
            if (linkedControlIndex !== -1) {
                featureType.attributes.linkedControls.splice(linkedControlIndex, 1, value);
            }
        }
        this.removeControl('styled', featureType, control.fieldName);
        control.label = value;
        control.name = value;
        control.fieldName = value;
        field.name = value;
    }

    // determines whether a control has been published to the back end or not
    isSavedControl(tool, control) {

        const savedControl = !this.isUnlocked && tool.updatedDateTime && this.lastSavedState[tool.toolId] && this.lastSavedState[tool.toolId][control.label];

        if (savedControl) {

            return control.controlTypeId === savedControl.controlTypeId
                && JSON.stringify(savedControl.attributes) === JSON.stringify(control.attributes);

        }

    }

    isBracketGetter(value) {
        return typeof value === 'string' && value.match(/^{.*}$/);
    }

    getBracketProperty(value) {
        return value && value.replace(/[{}]/g, '');
    }

    makeLabel(tool) {

        const featureType = tool.featureTypes[0],
            isPoint = featureType.attributes.interface === 'symbol',
            isLine = featureType.attributes.interface === 'polyline';

        const featureStyle = {
            name: tool.name + ' Label',
            featureStyleId: UE.randomId(),
            style: {
                type: 'symbol',
                layout: {
                    'symbol-placement': isLine ? 'line-center' : 'point',
                    'text-justify': 'center',
                    'text-anchor': isPoint ? 'top' : 'center',
                    'text-field': 'text',
                    'text-font': ['museo'],
                    'text-max-width': 10000,
                    'text-allow-overlap': true,
                    'text-ignore-placement': true,
                    'text-max-angle': 360,
                    'text-pitch-alignment': 'map',
                    'text-rotation-alignment': 'map',
                    'text-offset': isLine ? [0, -1] : isPoint ? [0, 0.75] : [0, 0]
                },
                paint: {
                    'text-color': '#303840',
                    'text-halo-color': '#ffffff',
                    'text-halo-width': 0.5
                }
            },
            featureType: {
                featureTypeId: featureType.featureTypeId
            }
        };

        if (isPoint) {
            featureStyle.style.paint['text-translate'] = [0, 4];
        }

        tool.featureStyles.push(featureStyle);

        return featureStyle.style;

    }

    setDefault(control, field, value) {
        if (value === undefined || value === '') {
            delete control.attributes.default;
            delete field.default;
        } else {
            control.attributes.default = value;
            field.default = value;
        }
    }

    removeLabel(tool) {

        const labelIndex = tool.featureStyles.length > 1
            ? tool.featureStyles.findIndex(f => f.style.layout && f.style.layout.hasOwnProperty('text-field'))
            : -1;

        if (labelIndex !== -1) {
            tool.featureStyles.splice(labelIndex, 1);
        }

    }

    toggleLabel(tool) {

        const labelIndex = tool.featureStyles.length > 1
            ? tool.featureStyles.findIndex(f => f.style.layout && f.style.layout.hasOwnProperty('text-field'))
            : -1;

        if (labelIndex !== -1) {
            tool.featureStyles.splice(labelIndex, 1);
        } else {
            this.makeLabel(tool);
        }

    }

    sync(obj, key) {

        return e => {

            obj[key] = e.target.value;

        };

    }

    getInterface(tool) {

        const featureType = tool.featureTypes[0];

        let toolInterface = featureType && featureType.attributes.interface;

        if (toolInterface === 'polygon') {

            toolInterface = tool.featureStyles.find(s => s.style.type === 'line')
                ? 'boundary'
                : 'fill';
        }

        return toolInterface;

    }

    editProjectTerm(value, projectTermType) {

        projectTermType = 'project' + projectTermType;

        if (value) {
            this.toolbox.attributes[projectTermType] = value;
        } else {
            delete this.toolbox.attributes[projectTermType];
        }

    }

    setToolInterface(tool, value) {

        const geometryType = {
            filepicker: 'Point',
            fill: 'Polygon',
            boundary: 'Polygon',
            polyline: 'LineString',
            symbol: 'Point',
            text: 'Point',
            project: 'Polygon',
            action: undefined,
            imodel: 'Point'
        }[value];

        let toolInterface = value;

        if (value === 'fill' || value === 'boundary') {

            toolInterface = 'polygon';

        }

        const icon = tool.assetForm.assetType.attributes.icon,
            featureType = tool.featureTypes[0],
            featureTypeId = featureType ? featureType.featureTypeId : UE.randomId(),
            oldInterface = featureType && featureType.attributes.interface;

        if (oldInterface === 'project' && toolInterface !== 'project') {
            this.projectToolCount--;
        } else if (oldInterface !== 'project' && toolInterface === 'project') {
            this.projectToolCount++;
        }

        if (geometryType || value === 'action') {

            tool.featureTypes = [{
                featureTypeId,
                name: tool.name,
                geometry: {
                    type: geometryType
                },
                properties: {},
                attributes: {
                    interface: toolInterface,
                    command: 'place',
                    linkedControls: [],
                    styledControls: [],
                    icon: {
                        mediaId: icon && icon.mediaId
                    }
                }
            }];

            tool.featureStyles = [{
                featureStyleId: UE.randomId(),
                name: tool.name,
                style: deepCloneObject(this.defaultStyles[value]),
                featureType: {
                    featureTypeId
                }
            }];

        } else {

            tool.featureTypes = [{
                featureTypeId,
                name: tool.name,
                attributes: {
                    interface: value,
                    command: 'place',
                    linkedControls: [],
                    styledControls: [],
                    icon: {
                        mediaId: icon && icon.mediaId
                    }
                },
                geometry: null,
                properties: {}
            }];

            tool.featureStyles = [];

        }

    }

    // returns a version of the value that varies depending on latitude
    // using the mercator projection
    geoSizeExpression(expression, isIconSize) {

        if (this.isGeoSizedExpression(expression)) {
            return expression;
        }

        let projection = ['/', expression, ['abs', ['/', ['*', 40075016.686, ['cos', ['get', '_latitude']]], 8589934592]]];

        if (isIconSize) {
            projection = ['/', projection, ['get', '_sourceWidthPx']];
        }

        return [
            'interpolate',
            ['exponential', 2],
            ['zoom'],
            0, 0,
            24, projection
        ];
    }

    // Given a style expression, determines whether that style is linked
    // to a form control, and if so returns the control label.
    getMatchedControl(styleKey, expression) {

        let matchedControl = '';
        const styleData = styleConfig[styleKey];

        if (styleData) {

            const featureProp = styleData.featureProp;
            const geoFeatureProp = styleData.geoFeatureProp;
            const isIconSize = styleKey === 'icon-size';

            if (expression) {
                if (expression.property
                    && expression.property !== featureProp
                    && expression.property !== geoFeatureProp) {
                    matchedControl = expression.property;
                } else if (expression[0] === 'match') {
                    matchedControl = expression[1][1];
                } else if (expression[0] === 'get'
                    && expression[1] !== geoFeatureProp
                    && expression[1] !== featureProp) {
                    matchedControl = expression[1];
                } else if (this.isGeoSizedExpression(expression)) {
                    const geoSizedExpression = this.getGeoSizedExpression(expression, isIconSize);
                    return this.getMatchedControl(styleKey, geoSizedExpression);
                } else if (isIconSize) {
                    return this.getMatchedControl(styleKey, expression[1]);
                }
            }
        }

        return matchedControl;

    }

    // Given a value that is geo-sized, returns a
    // non-geo-sized version of the value
    getGeoSizedExpression(value, isIconSize) {
        if (value) {
            return isIconSize ? value[6][1][1] : value[6][1];
        }
        return value;
    }

    // If the value is geo-sized, removes the geo-sizing logic
    // If the value is an icon-size, adds the logic to divide by _sourceWidthPx
    unGeoSizeExpression(expression, isIconSize) {

        if (this.isGeoSizedExpression(expression)) {
            expression = this.getGeoSizedExpression(expression, isIconSize);
        }

        if (isIconSize && expression && expression[0] !== '/' && expression[0] !== 'match') {
            expression = ['/', expression, ['get', '_sourceWidthPx']];
        }

        return expression;
    }

    removeDupeToolStyleNames(tool) {
        const checkedFeatureStyles = [];
        const checkedNames = {};
        for (let i = 0; i < tool.featureStyles.length; i++) {
            const styleToCheck = tool.featureStyles[i];
            if (checkedNames[styleToCheck.name]) {
                // This is a dupe, ignore!
                console.log('duplicate style removed', styleToCheck);
            } else {
                checkedNames[styleToCheck.name] = true;
                checkedFeatureStyles.push(styleToCheck);
            }
        }
        tool.featureStyles = [...checkedFeatureStyles];
    }

    // If any styles are linked to this control,
    // reset them to their default state
    removeLinkedStyleControl(controlLabel, {layout = {}, paint = {}}) {
        [layout, paint].forEach(styleGroup => {
            Object.keys(styleGroup).forEach(styleKey => {
                const expression = this.unGeoSizeExpression(styleGroup[styleKey], styleKey === 'icon-size');
                if (Array.isArray(expression)) {
                    const matchedControl = this.getMatchedControl(styleKey, expression);
                    if (matchedControl === controlLabel) {
                        if (styleConfig[styleKey].defaultValue) {
                            styleGroup[styleKey] = styleConfig[styleKey].defaultValue;
                        } else {
                            delete styleGroup[styleKey];
                        }
                    }
                }
            });
        });
    }

    controlTypeToFieldType(type, values = []) {
        return {
            area: 'area',
            attachments: 'attachments',
            color: 'color',
            comments: {
                type: 'objRef',
                objType: 'thread'
            },
            coordinates: {
                type: 'geometry',
                geomType: 'Point'
            },
            date: 'datetime',
            dateTime: 'datetime',
            dropdown: {
                type: 'enum',
                values
            },
            file: {
                type: 'array',
                items: {
                    type: 'objRef',
                    objType: 'media'
                }
            },
            length: 'length',
            multiselect: {
                type: 'array',
                items: {
                    type: 'enum',
                    values
                }
            },
            multitext: {
                type: 'array',
                items: {
                    type: 'text'
                }
            },
            multiuser: {
                type: 'array',
                items: {
                    type: 'objRef',
                    objType: 'user'
                }
            },
            name: 'name',
            number: 'number',
            paragraph: 'paragraph',
            plan: {
                type: 'objRef',
                objType: 'plan'
            },
            project: {
                type: 'objRef',
                objType: 'project'
            },
            radio: {
                type: 'enum',
                values
            },
            request: 'text',
            survey: {
                type: 'objRef',
                objType: 'survey'
            },
            text: 'text',
            toggle: 'boolean',
            URL: {
                type: 'object',
                properties: {
                    URL: 'text',
                    display: 'text'
                }
            },
            user: {
                type: 'objRef',
                objType: 'user'
            },
            volume: 'volume',
            place: {
                type: 'object',
                properties: {
                    levelIds: {
                        type: 'array',
                        items: {
                            type: 'objRef',
                            objType: 'level'
                        }
                    },
                    placeIds: {
                        type: 'array',
                        items: {
                            type: 'objRef',
                            objType: 'place'
                        }
                    }
                }
            },
            asset: {
                type: 'array',
                items: {
                    type: 'objRef',
                    objType: 'asset'
                }
            },
            imodel: {
                type: 'object',
                properties: {
                    iModelId: {
                        type: 'text'
                    },
                    bentleyProjectId: {
                        type: 'text'
                    }
                }
            }
        }[type];
    }

    changeControlType({control, field, controlTypeId, tool}) {

        control.controlTypeId = controlTypeId;
        const controlType = constants.controlTypeIdToName[controlTypeId];
        const featureType = tool.featureTypes && tool.featureTypes[0];
        const featureStyle = tool.featureStyles && tool.featureStyles[0];
        const values = field.type.values || field.type.items && field.type.items.values || ['Option 1'];
        field.type = this.controlTypeToFieldType(controlType, values);

        if (this.doIndex[controlType]) {
            field.index = true;
        } else {
            delete field.index;
        }

        delete field.restricted;
        delete field.default;

        const attributes = control.attributes;

        delete attributes.min;
        delete attributes.max;
        delete attributes.minFiles;
        delete attributes.maxFiles;
        delete attributes.accept;
        delete attributes.default;
        delete attributes.defaultToToday;
        delete attributes.defaultToBlank;
        delete attributes.url;
        delete attributes.parse;
        delete attributes.readOnly;
        delete attributes.hideLabel;
        delete attributes.type;
        delete attributes.mediaId;

        if (controlType === 'file') {
            attributes.minFiles = 1;
            attributes.maxFiles = 1;
        }

        if (controlType === 'toggle') {
            this.setDefault(control, field, false);
        }

        if (controlType === 'request') {
            attributes.readOnly = true;
        }

        // Set default linked controls based on control type
        if (controlType === 'coordinates') {
            this.setLinkedControls(featureType, control, field, true);
        } else if (controlType === 'project') {
            control.attributes.hidden = true;
            this.changeControlLabel(PROJECT_CONTROL_LABEL, control, field, featureType);
            this.setLinkedControls(featureType, control, field, true);
        } else {
            // Turn off any linked controls that were previously set
            this.setLinkedControls(featureType, control, field, false);
        }

        if (this.sharedColumns[control.name]) {
            this.revalidateSharedColumn(control.name);
        }

        if (featureStyle && featureStyle.style) {
            this.removeLinkedStyleControl(control.label, featureStyle.style);
        }

        m.redraw();
    }

    twoDigits(n) {

        if (n.toString().length === 1) {
            n = '0' + n;
        }

        return n;

    }

    formatDate(date) {

        if (date) {

            date = new Date(date);

            return `${date.getFullYear()}-${this.twoDigits(date.getMonth() + 1)}-${this.twoDigits(date.getDate())}`;

        }

    }

    setFileType(control, value) {

        if (value) {

            control.attributes.accept = [value];

        } else {

            delete control.attributes.accept;

        }

    }

    setLinkedControls(featureType, control, field, doLink) {
        featureType.attributes.linkedControls = featureType.attributes.linkedControls || [];

        const linkedControls = featureType.attributes.linkedControls;

        if (doLink) {
            linkedControls.push(control.label);
            this.setDefault(control, field);
        } else {
            const index = linkedControls.indexOf(control.label);
            if (index > -1) {
                linkedControls.splice(linkedControls.indexOf(control.label), 1);
            }
        }
    }

    getAvailableControls(controls, selectedControlTypeId) {
        return Object.keys(constants.controlTypeIdToName)
            .filter(controlTypeId => {
                if (this.isCommentsOrLinks[controlTypeId]
                    || this.isEmbedControl(controlTypeId)) {
                    return false;
                }
                if (selectedControlTypeId !== controlTypeId && this.controlOneAllowed[controlTypeId]) {
                    return !controls.find(control => control.controlTypeId === controlTypeId);
                }
                return true;
            });
    }

    getControlDisplayName(controlTypeId) {
        if (controlTypeId === constants.controlTypeNameToId.asset) {
            return 'Link';
        }
        const name = constants.controlTypeIdToName[controlTypeId];
        return name[0].toUpperCase() + name.slice(1);
    }

    addControl(tool, label, type = 'text', values) {
        const controlCount = tool.assetForm.controls.filter(control => !this.isCommentsOrLinks[control.controlTypeId]).length + 1;

        const controlLabel = label || 'New Property ' + controlCount;

        const control = {
            name: controlLabel,
            label: controlLabel,
            fieldName: controlLabel,
            controlTypeId: controlTypeNameToId[type],
            attributes: {}
        };

        const newField = {
            name: controlLabel,
            type: this.controlTypeToFieldType(type, values),
            index: this.doIndex[type]
        };

        if (type === 'file') {
            control.attributes.maxFiles = 1;
            control.attributes.minFiles = 1;
        }

        tool.assetForm.controls.push(control);

        tool.assetForm.assetType.fields.push(newField);

    }

    isEmbedControl(controlTypeId) {
        return constants.controlTypeIdToName[controlTypeId] === 'embed';
    }

    addFormEmbedControl(tool) {
        const embedCount = tool.assetForm.controls.filter(control => this.isEmbedControl(control.controlTypeId)).length + 1;
        const embedControlLabel = 'New Form Element ' + embedCount;

        const control = {
            name: embedControlLabel,
            label: embedControlLabel,
            fieldName: embedControlLabel,
            controlTypeId: controlTypeNameToId.embed
        };

        this.changeEmbedControl(control, 'image');

        tool.assetForm.controls.push(control);
    }

    changeEmbedControl(control, type) {
        switch (type) {
        case 'header': {
            control.attributes = {
                type: 'header',
                hideLabel: true,
                text: control.attributes && control.attributes.text ? control.attributes.text : ''
            };
            break;
        }
        case 'notes': {
            control.attributes = {
                type: 'notes',
                hideLabel: true,
                text: control.attributes && control.attributes.text ? control.attributes.text : ''
            };
            break;
        }
        case 'image': {
            control.attributes = {
                type: 'image',
                hideLabel: true,
                mediaId: constants.predefinedEmbeds.dashed.mediaId
            };
            break;
        }
        default:
            break;
        }
    }

    updateTextEmbed(control, value) {
        control.attributes.text = value;
    }

    changeEmbedControlLabel(value, control) {
        control.label = value;
        control.name = value;
        control.fieldName = value;
    }

    removeEmbedControl(control, tool) {
        const index = tool.assetForm.controls.indexOf(control.label);
        if (index !== -1) {
            tool.assetForm.controls.splice(index, 1);
        }
    }

    blankTool(type = 'Point', toolName = 'New Tool') {

        const featureTypeId = UE.randomId();

        return {
            toolId: UE.randomId(),
            name: toolName,
            attributes: {
                styleZOrder: []
            },
            assetForm: {
                assetFormId: UE.randomId(),
                name: toolName,
                attributes: {},
                controls: [{
                    name: 'Comments',
                    label: 'Comments',
                    controlTypeId: controlTypeNameToId.comments,
                    fieldName: 'Comments',
                    attributes: {}
                },
                {
                    name: 'Attachments',
                    label: 'Attachments',
                    controlTypeId: controlTypeNameToId.links,
                    fieldName: 'Attachments',
                    attributes: {}
                }],
                layout: {},
                assetType: {
                    assetTypeId: UE.randomId(),
                    name: toolName,
                    attributes: {
                        icon: {}
                    },
                    constraints: [],
                    fields: [{
                        name: 'Comments',
                        type: 'comments'
                    },
                    {
                        name: 'Attachments',
                        type: 'attachments'
                    }]
                }
            },
            featureStyles: [{
                featureStyleId: UE.randomId(),
                name: toolName,
                style: deepCloneObject(this.defaultStyles.symbol),
                featureType: {
                    featureTypeId
                }
            }],
            featureTypes: [{
                featureTypeId,
                name: toolName,
                attributes: {
                    icon: {},
                    command: 'place',
                    interface: {
                        'Point': 'symbol',
                        'LineString': 'polyline',

                        'Polygon': 'polygon'
                    }[type],
                    linkedControls: [],
                    styledControls: []
                },
                geometry: {
                    type
                },
                properties: {}
            }]
        };
    }

    createTool(toolGroup) {

        toolGroup.tools.push(this.blankTool());

    }

    selectToolbox(featureType) {

        modal(ToolboxPicker, {
            select: 'toolbox',
            onSelect: (item) => {
                featureType.attributes.toolbox = item.toolboxId || item.id;
                modal.close();
            }
        });

    }

    enterToolboxId(featureType) {
        modal(ToolboxId, {
            toolboxId: featureType.attributes.toolbox, done: (id) => {
                featureType.attributes.toolbox = id[0];
            }
        });
    }

    clearToolbox(featureType) {
        delete featureType.attributes.toolbox;
    }

    createToolGroup(name) {

        const toolGroup = {
            toolGroupId: UE.randomId(),
            name: name || 'New Tool Group',
            attributes: {
                toolZOrder: [],
                icon: {}
            },
            tools: []
        };

        this.toolbox.group.groups.push(toolGroup);

        return toolGroup;

    }

    clone(pickerType, destination) {

        modal(ToolboxPicker, {
            select: pickerType,
            onSelect: (item) => {
                destination.push(item);
                modal.close();
            }
        });

    }

    cloneToolbox(toolbox) {
        const clonedToolbox = {
            account: {
                accountId: constants.unearthAccountId
            },
            toolboxId: UE.randomId(),
            attributes: {icon: toolbox.attributes.icon},
            name: toolbox.name,
            group: {
                toolGroupId: UE.randomId(),
                groups: []
            }
        };
        clonedToolbox.group.groups = toolbox.group.groups.map(toolGroup => this.cloneToolGroup(toolGroup));
        return clonedToolbox;
    }

    cloneToolGroup(toolGroup) {
        toolGroup = deepCloneObject(toolGroup);
        delete toolGroup.updatedDateTime;
        delete toolGroup.createdDateTime;
        toolGroup.toolGroupId = UE.randomId();
        const toolsToClone = toolGroup.tools.filter(tool => tool.name !== 'Comment');
        toolGroup.tools = toolsToClone.map(tool => this.cloneTool(tool));
        return toolGroup;
    }

    /*
        Required Items to clone a new tool:
    
        const featureTypeId = UE.randomId();
        const clonedItem = deepCloneObject(item);
        clonedItem.toolId = UE.randomId();
        clonedItem.assetForm.assetFormId = UE.randomId();
        clonedItem.assetForm.assetType.assetTypeId = UE.randomId();
        clonedItem.featureStyles[0].featureStyleId = UE.randomId();
        clonedItem.featureStyles[0].featureTypeId = featureTypeId;
        clonedItem.featureStyles[0].featureType = {featureTypeId};
        clonedItem.featureTypes[0].featureTypeId = featureTypeId;
        delete clonedItem.assetForm.assetType.base;
        delete clonedItem.assetForm.createdDateTime;
        delete clonedItem.assetForm.updatedDateTime;
        delete clonedItem.assetForm.assetType.createdDateTime;
        delete clonedItem.assetForm.assetType.updatedDateTime;
        return clonedItem;
    */

    cloneTool(tool) {
        const oldToolName = tool.name;
        tool = deepCloneObject(tool);
        tool.toolId = UE.randomId();
        tool.assetForm.assetFormId = UE.randomId();
        const assetType = tool.assetForm.assetType;
        assetType.assetTypeId = UE.randomId();
        delete assetType.createdDateTime;
        delete assetType.updatedDateTime;
        tool.assetForm.name = tool.name;
        delete tool.updatedDateTime;
        delete tool.createdDateTime;
        delete assetType.base;
        const featureType = tool.featureTypes[0];
        if (featureType) {
            const featureTypeId = UE.randomId();
            delete featureType.createdDateTime;
            delete featureType.updatedDateTime;
            featureType.featureTypeId = featureTypeId;
            featureType.name = tool.name;
            tool.featureStyles.forEach(featureStyle => {
                delete featureStyle.createdDateTime;
                delete featureStyle.updatedDateTime;
                featureStyle.featureStyleId = UE.randomId();
                featureStyle.featureType.featureTypeId = featureTypeId;
                featureStyle.name = featureStyle.name.replace(oldToolName, tool.name);
            });
        }
        return tool;
    }

    isValidJson(str) {
        try {
            JSON.parse(str);
            return true;
        } catch (e) {
            return false;
        }
    }

    toggleToolGroupFeatureFlag(feature, toolGroup) {
        if (toolGroup.attributes.featureFlags) {
            if (toolGroup.attributes.featureFlags[feature]) {
                delete toolGroup.attributes.featureFlags[feature];
            } else {
                toolGroup.attributes.featureFlags[feature] = true;
            }
        } else {
            toolGroup.attributes.featureFlags = {[feature]: true};
        }
    }

    toggleFeatureFlag(feature) {
        const toolbox = this.toolbox;
        if (toolbox.attributes.featureFlags) {
            if (toolbox.attributes.featureFlags[feature]) {
                delete toolbox.attributes.featureFlags[feature];
            } else {
                toolbox.attributes.featureFlags[feature] = true;
            }
        } else {
            toolbox.attributes.featureFlags = {[feature]: true};
        }
    }

    featureFlagDisabledMessage(feature) {
        if (feature === TOOLKIT_MODIFY_FLAG && this.hasCustomReports()) {
            return '*In-app toolkit modifications are always disabled for toolkits with custom reports.';
        }
        if (feature === BATCH_MODIFY_FLAG && !this.isUnlocked) {
            return '*Batch Modify cannot be edited after publish.';
        }
    }

    isMetaProject() {
        const {toolbox} = this;
        return toolbox.group.groups.find(g =>
            g.tools.find(t => t.featureTypes && t.featureTypes.find(ft => ft.attributes && ft.attributes.interface === 'project'))
        );
    }

    removeToolGroup(toolGroupId) {
        const index = this.toolbox.group.groups.findIndex(tg => tg.toolGroupId === toolGroupId);
        this.toolbox.group.groups.splice(index, 1);
        m.redraw();
    }

    removeTool(toolGroup, toolId) {
        const index = toolGroup.tools.findIndex(t => t.toolId === toolId);
        toolGroup.tools.splice(index, 1);
        m.redraw();
    }

}

export default new ToolboxModel();
